import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input, InputGroup,
    InputRightElement, HStack, VStack, useToast, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, FormErrorMessage, Select, SimpleGrid,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent,
    AlertDialogOverlay, Spinner, Divider, Badge
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon, ViewIcon, SearchIcon } from "@chakra-ui/icons";
import { getClientes, createCliente, updateCliente, deleteCliente } from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import ScrollToTop from "../components/ScrollToTop";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialFormState = {
    id: null,
    nome: "",
    documento: "",
    tipo: "1",
    email: "",
    telefone: "",
    ie: "",
    nickname: "",
    enderecos: []
};

const Clientes = () => {
    const toast = useToast();
    const cancelRef = useRef();

    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("search") || "";
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationMeta, setPaginationMeta] = useState(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormState);
    const [viewData, setViewData] = useState(null);

    const [confirmAlert, setConfirmAlert] = useState({ isOpen: false, actionType: "", targetId: null });
    const [processing, setProcessing] = useState(false);

    const fetchClientesList = useCallback(async () => {
        setLoading(true);
        const params = {
            page: currentPage,
            search: debouncedSearchTerm,
        };
        const response = await getClientes(params);
        if (response) {
            setClientes(response.data);
            setPaginationMeta({
                current_page: response.current_page,
                last_page: response.last_page,
                total: response.total
            });
        }
        setLoading(false);
    }, [currentPage, debouncedSearchTerm]);

    useEffect(() => {
        fetchClientesList();
    }, [fetchClientesList]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    const formatDoc = (value) => {
        const cleaned = value.replace(/\D/g, "");
        if (cleaned.length <= 11) {
            return cleaned.replace(/^(\d{3})(\d)/, "$1.$2")
                .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
                .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, "$1.$2.$3-$4")
                .slice(0, 14);
        }
        return cleaned.replace(/^(\d{2})(\d)/, "$1.$2")
            .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
            .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, "$1.$2.$3/$4-$5")
            .slice(0, 18);
    };

    const formatPhone = (value) => {
        const cleaned = value.replace(/\D/g, "");
        if (cleaned.length <= 10) return cleaned.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
        return cleaned.replace(/^(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
    };

    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleAddressChange = (index, field, value) => {
        const updated = [...formData.enderecos];
        updated[index][field] = value;
        setFormData({ ...formData, enderecos: updated });
    };

    const addAddressBlock = () => {
        setFormData({
            ...formData,
            enderecos: [...formData.enderecos, { id: "", cep: "", uf: "", cidade: "", bairro: "", endereco: "", numero: "", complemento: "" }]
        });
    };

    const removeAddressBlock = (index) => {
        const updated = formData.enderecos.filter((_, i) => i !== index);
        setFormData({ ...formData, enderecos: updated });
    };

    const fetchCep = async (index, cep) => {
        const cleanedCep = cep.replace(/\D/g, "");
        if (cleanedCep.length === 8) {
            try {
                const res = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
                const data = await res.json();
                if (!data.erro) {
                    const updated = [...formData.enderecos];
                    updated[index].uf = data.uf;
                    updated[index].cidade = data.localidade;
                    updated[index].bairro = data.bairro;
                    updated[index].endereco = data.logradouro;
                    setFormData({ ...formData, enderecos: updated });
                }
            } catch (err) {}
        }
    };

    const openCreateModal = () => {
        setFormData(initialFormState);
        setIsFormModalOpen(true);
    };

    const openEditModal = (cliente) => {
        setFormData({
            id: cliente.id,
            nome: cliente.nome || "",
            documento: cliente.documento || "",
            tipo: cliente.tipo || "1",
            email: cliente.email || "",
            telefone: cliente.telefone || "",
            ie: cliente.ie || "",
            nickname: cliente.nickname || "",
            enderecos: cliente.enderecos || []
        });
        setIsFormModalOpen(true);
    };

    const openViewModal = (cliente) => {
        setViewData(cliente);
        setIsViewModalOpen(true);
    };

    const validarFormulario = () => {
        if (!formData.nome.trim()) return "O campo Nome é obrigatório.";
        if (formData.email && !EMAIL_REGEX.test(formData.email.trim())) {
            return "E-mail inválido. Remova espaços e use o formato nome@dominio.com.";
        }
        if (formData.documento) {
            const digitos = formData.documento.replace(/\D/g, "");
            if (formData.tipo === "1" && digitos.length !== 11) {
                return "CPF inválido. Deve conter 11 dígitos. Confira o Tipo de Pessoa selecionado.";
            }
            if (formData.tipo === "2" && digitos.length !== 14) {
                return "CNPJ inválido. Deve conter 14 dígitos. Confira o Tipo de Pessoa selecionado.";
            }
        }
        return null;
    };

    const requestSave = () => {
        const erro = validarFormulario();
        if (erro) {
            toast({ title: "Erro de Validação", description: erro, status: "warning", isClosable: true });
            return;
        }
        setConfirmAlert({ isOpen: true, actionType: "save", targetId: null });
    };

    const requestDelete = (id) => {
        setConfirmAlert({ isOpen: true, actionType: "delete", targetId: id });
    };

    const executeAction = async () => {
        setProcessing(true);
        if (confirmAlert.actionType === "save") {
            const payload = { ...formData };
            let response;
            if (formData.id) {
                response = await updateCliente(formData.id, payload);
            } else {
                response = await createCliente(payload);
            }

            if (response && response.data && response.data.status) {
                toast({ title: "Sucesso", description: formData.id ? "Cliente atualizado." : "Cliente criado.", status: "success", isClosable: true });
                setIsFormModalOpen(false);
                fetchClientesList();
            } else {
                const respData = response?.data;
                const validationMsg = respData?.errors ? Object.values(respData.errors).flat()[0] : null;
                toast({ title: "Erro", description: validationMsg || respData?.message || respData?.error || "Ocorreu um erro ao salvar.", status: "error", isClosable: true });
            }
        } else if (confirmAlert.actionType === "delete") {
            const response = await deleteCliente(confirmAlert.targetId);
            if (response && response.data && response.data.status) {
                toast({ title: "Sucesso", description: "Cliente removido.", status: "success", isClosable: true });
                fetchClientesList();
            } else {
                toast({ title: "Erro", description: response?.data?.error || "Não foi possível remover.", status: "error", isClosable: true });
            }
        }
        setProcessing(false);
        setConfirmAlert({ isOpen: false, actionType: "", targetId: null });
    };

    return (
        <Box bg="gray.50" minH="100vh" p={{ base: 4, md: 8 }}>
            <ScrollToTop />
            <Box maxW="1200px" mx="auto" bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
                    <Text fontSize="2xl" fontWeight="bold" color="orange.500">Gestão de Clientes</Text>
                    <Button leftIcon={<AddIcon />} colorScheme="orange" onClick={openCreateModal}>
                        Novo Cliente
                    </Button>
                </Flex>

                <Flex mb={4}>
                    <InputGroup maxW="400px">
                        <Input 
                            placeholder="Buscar por nome, documento ou e-mail..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                        />
                        <InputRightElement><SearchIcon color="gray.400" /></InputRightElement>
                    </InputGroup>
                </Flex>

                <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                        <Thead bg="gray.100">
                            <Tr>
                                <Th>ID</Th>
                                <Th>Nome</Th>
                                <Th>Nickname</Th>
                                <Th>Documento</Th>
                                <Th>Email</Th>
                                <Th>Telefone</Th>
                                <Th textAlign="center">Ações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {loading ? (
                                <Tr><Td colSpan={7} textAlign="center"><Spinner my={4} /></Td></Tr>
                            ) : clientes.length === 0 ? (
                                <Tr><Td colSpan={7} textAlign="center">Nenhum cliente encontrado.</Td></Tr>
                            ) : (
                                clientes.map((cli) => (
                                    <Tr key={cli.id} _hover={{ bg: "gray.50" }}>
                                        <Td>{cli.id}</Td>
                                        <Td fontWeight="medium">{cli.nome}</Td>
                                        <Td>{cli.nickname || "-"}</Td>
                                        <Td>{cli.documento || "-"}</Td>
                                        <Td>{cli.email || "-"}</Td>
                                        <Td>{cli.telefone || "-"}</Td>
                                        <Td textAlign="center">
                                            <HStack spacing={2} justify="center">
                                                <IconButton size="sm" icon={<ViewIcon />} colorScheme="blue" variant="ghost" onClick={() => openViewModal(cli)} />
                                                <IconButton size="sm" icon={<EditIcon />} colorScheme="green" variant="ghost" onClick={() => openEditModal(cli)} />
                                                <IconButton size="sm" icon={<DeleteIcon />} colorScheme="red" variant="ghost" onClick={() => requestDelete(cli.id)} />
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </Box>

                {paginationMeta && paginationMeta.last_page > 1 && (
                    <Flex justify="space-between" align="center" mt={4}>
                        <Button size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} isDisabled={currentPage === 1}>Anterior</Button>
                        <Text fontSize="sm">Página {currentPage} de {paginationMeta.last_page}</Text>
                        <Button size="sm" onClick={() => setCurrentPage(p => Math.min(paginationMeta.last_page, p + 1))} isDisabled={currentPage === paginationMeta.last_page}>Próximo</Button>
                    </Flex>
                )}
            </Box>

            <Modal isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)} size="3xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="orange.500">{formData.id ? "Editar Cliente" : "Novo Cliente"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">
                            <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                                <Text fontWeight="bold" mb={3}>Dados Principais</Text>
                                <SimpleGrid columns={[1, 2]} spacing={4}>
                                    <FormControl isRequired>
                                        <FormLabel>Nome</FormLabel>
                                        <Input bg="white" value={formData.nome} onChange={(e) => handleFormChange("nome", e.target.value)} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Tipo de Pessoa</FormLabel>
                                        <Select bg="white" value={formData.tipo} onChange={(e) => handleFormChange("tipo", e.target.value)}>
                                            <option value="1">Pessoa Física (CPF)</option>
                                            <option value="2">Pessoa Jurídica (CNPJ)</option>
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Documento</FormLabel>
                                        <Input bg="white" value={formData.documento} onChange={(e) => handleFormChange("documento", formatDoc(e.target.value))} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Inscrição Estadual (IE)</FormLabel>
                                        <Input bg="white" value={formData.ie} onChange={(e) => handleFormChange("ie", e.target.value.replace(/\D/g, ""))} />
                                    </FormControl>
                                    <FormControl isInvalid={!!formData.email && !EMAIL_REGEX.test(formData.email.trim())}>
                                        <FormLabel>E-mail</FormLabel>
                                        <Input type="email" bg="white" value={formData.email} onChange={(e) => handleFormChange("email", e.target.value)} />
                                        <FormErrorMessage>Remova espaços e use o formato nome@dominio.com.</FormErrorMessage>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Telefone</FormLabel>
                                        <Input bg="white" value={formData.telefone} onChange={(e) => handleFormChange("telefone", formatPhone(e.target.value))} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Apelido / Nickname</FormLabel>
                                        <Input bg="white" value={formData.nickname} onChange={(e) => handleFormChange("nickname", e.target.value)} />
                                    </FormControl>
                                </SimpleGrid>
                            </Box>

                            <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Text fontWeight="bold">Endereços</Text>
                                    <Button size="sm" leftIcon={<AddIcon />} colorScheme="teal" onClick={addAddressBlock}>Adicionar Endereço</Button>
                                </Flex>
                                {formData.enderecos.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500">Nenhum endereço cadastrado.</Text>
                                ) : (
                                    <VStack spacing={4}>
                                        {formData.enderecos.map((end, index) => (
                                            <Box key={index} p={4} borderWidth="1px" borderRadius="md" bg="white" w="100%">
                                                <Flex justify="space-between" align="center" mb={3}>
                                                    <Badge colorScheme="blue">Endereço {index + 1}</Badge>
                                                    <IconButton size="xs" icon={<DeleteIcon />} colorScheme="red" onClick={() => removeAddressBlock(index)} />
                                                </Flex>
                                                <SimpleGrid columns={[1, 3]} spacing={4}>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs">CEP</FormLabel>
                                                        <Input size="sm" value={end.cep} onChange={(e) => {
                                                            let val = e.target.value.replace(/\D/g, "").replace(/^(\d{5})(\d)/, "$1-$2").slice(0, 9);
                                                            handleAddressChange(index, "cep", val);
                                                        }} onBlur={(e) => fetchCep(index, e.target.value)} />
                                                    </FormControl>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs">Estado (UF)</FormLabel>
                                                        <Input size="sm" value={end.uf} onChange={(e) => handleAddressChange(index, "uf", e.target.value)} />
                                                    </FormControl>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs">Cidade</FormLabel>
                                                        <Input size="sm" value={end.cidade} onChange={(e) => handleAddressChange(index, "cidade", e.target.value)} />
                                                    </FormControl>
                                                </SimpleGrid>
                                                <SimpleGrid columns={[1, 3]} spacing={4} mt={3}>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs">Bairro</FormLabel>
                                                        <Input size="sm" value={end.bairro} onChange={(e) => handleAddressChange(index, "bairro", e.target.value)} />
                                                    </FormControl>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs">Rua / Endereço</FormLabel>
                                                        <Input size="sm" value={end.endereco} onChange={(e) => handleAddressChange(index, "endereco", e.target.value)} />
                                                    </FormControl>
                                                    <FormControl>
                                                        <FormLabel fontSize="xs">Número</FormLabel>
                                                        <Input size="sm" value={end.numero} onChange={(e) => handleAddressChange(index, "numero", e.target.value)} />
                                                    </FormControl>
                                                </SimpleGrid>
                                                <FormControl mt={3}>
                                                    <FormLabel fontSize="xs">Complemento</FormLabel>
                                                    <Input size="sm" value={end.complemento || ""} onChange={(e) => handleAddressChange(index, "complemento", e.target.value)} />
                                                </FormControl>
                                            </Box>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setIsFormModalOpen(false)}>Cancelar</Button>
                        <Button colorScheme="orange" onClick={requestSave}>Salvar Cliente</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="orange.500">Detalhes do Cliente</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {viewData && (
                            <VStack align="stretch" spacing={4}>
                                <Box p={4} bg="gray.50" borderRadius="md" borderWidth="1px">
                                    <SimpleGrid columns={2} spacing={3}>
                                        <Text fontSize="sm"><b>ID:</b> {viewData.id}</Text>
                                        <Text fontSize="sm"><b>Nome:</b> {viewData.nome}</Text>
                                        <Text fontSize="sm"><b>Documento:</b> {viewData.documento || "-"}</Text>
                                        <Text fontSize="sm"><b>Tipo:</b> {viewData.tipo === "1" ? "Física" : "Jurídica"}</Text>
                                        <Text fontSize="sm"><b>E-mail:</b> {viewData.email || "-"}</Text>
                                        <Text fontSize="sm"><b>Telefone:</b> {viewData.telefone || "-"}</Text>
                                        <Text fontSize="sm"><b>IE:</b> {viewData.ie || "-"}</Text>
                                        <Text fontSize="sm"><b>Nickname:</b> {viewData.nickname || "-"}</Text>
                                    </SimpleGrid>
                                </Box>
                                <Text fontWeight="bold">Endereços Registrados</Text>
                                {viewData.enderecos && viewData.enderecos.length > 0 ? (
                                    viewData.enderecos.map((end, idx) => (
                                        <Box key={idx} p={3} bg="white" borderRadius="md" borderWidth="1px">
                                            <Badge mb={2}>Endereço {idx + 1}</Badge>
                                            <Text fontSize="sm">{end.endereco}, {end.numero} {end.complemento ? `- ${end.complemento}` : ""}</Text>
                                            <Text fontSize="sm">{end.bairro} - {end.cidade}/{end.uf}</Text>
                                            <Text fontSize="sm">CEP: {end.cep}</Text>
                                        </Box>
                                    ))
                                ) : (
                                    <Text fontSize="sm" color="gray.500">Nenhum endereço encontrado.</Text>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" onClick={() => setIsViewModalOpen(false)}>Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AlertDialog isOpen={confirmAlert.isOpen} leastDestructiveRef={cancelRef} onClose={() => setConfirmAlert({ isOpen: false, actionType: "", targetId: null })}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {confirmAlert.actionType === "save" ? "Confirmar Gravação" : "Confirmar Exclusão"}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {confirmAlert.actionType === "save" 
                                ? "Tem certeza que deseja salvar os dados deste cliente?" 
                                : "Tem certeza que deseja remover este cliente? Esta ação não pode ser desfeita e removerá os vínculos de endereço."}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setConfirmAlert({ isOpen: false, actionType: "", targetId: null })} isDisabled={processing}>
                                Cancelar
                            </Button>
                            <Button colorScheme={confirmAlert.actionType === "save" ? "orange" : "red"} onClick={executeAction} ml={3} isLoading={processing}>
                                Confirmar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default Clientes;